import * as React from 'react';

import * as SliderPrimitive from '@radix-ui/react-slider';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { cn } from './utils';

const DIM_RAINBOW = `linear-gradient(90deg,
  rgba(84, 209, 136, 0.5) 0%,
  rgba(244, 228, 76, 0.5) 33%,
  rgba(252, 191, 36, 0.5) 66%,
  rgba(238, 74, 28, 0.5) 100%)`;

const FULL_RAINBOW = `linear-gradient(90deg,
  rgba(84, 209, 136, 1.0) 0%,
  rgba(244, 228, 76, 1.0) 33%,
  rgba(252, 191, 36, 1.0) 66%,
  rgba(238, 74, 28, 1.0) 100%)`;

const SOLID_DIM: { [key: string]: string } = {
  primary: 'bg-primary/20',
  slate: 'bg-slate-500/20',
};
const SOLID_FULL: { [key: string]: string } = {
  primary: 'bg-primary',
  slate: 'bg-slate-500',
};

export type SliderProps = {
  /** Variant: 'solid' (default) or 'rainbow' (green-yellow-orange-red gradient) */
  variant?: 'solid' | 'rainbow';
  /** Color scheme for solid variant */
  color?: keyof typeof SOLID_DIM;
  /** Floor value - cannot slide below this. Area below is grayed out. */
  floor?: number;
  /** Custom classes for the thumb element */
  thumbClassName?: string;
  /** Classes applied to thumb when dragging */
  draggingThumbClassName?: string;
  /** Tooltip text shown on the thumb during hover/drag/focus */
  tooltipContent?: string;
  /** Tooltip text shown when pointer is over the shielded (floor) region */
  tooltipContentOnShield?: string;
} & React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>;

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      value,
      defaultValue,
      variant = 'solid',
      color = 'primary',
      floor,
      min = 0,
      max = 100,
      onValueChange,
      thumbClassName: thumbClassNameProp,
      draggingThumbClassName,
      tooltipContent,
      tooltipContentOnShield,
      ...props
    },
    ref,
  ) => {
    const [dragging, setDragging] = React.useState(false);
    const [isHovering, setIsHovering] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHoveringShield, setIsHoveringShield] = React.useState(false);
    const sliderRef = React.useRef<HTMLElement | null>(null);

    const domainMin = min;
    const domainMax = max;
    const clampMin = floor != null ? Math.max(floor, domainMin) : domainMin;

    // Ensure value is always an array and valid
    const normalizedValue = React.useMemo(() => {
      if (Array.isArray(value)) {
        return value.map(v => (typeof v === 'number' && !isNaN(v) ? Math.max(v, clampMin) : clampMin));
      }
      if (typeof value === 'number' && !isNaN(value)) {
        return [Math.max(value, clampMin)];
      }
      return [clampMin];
    }, [value, clampMin]);

    const normalizedDefaultValue = React.useMemo(() => {
      if (Array.isArray(defaultValue)) {
        return defaultValue.map(v => (typeof v === 'number' && !isNaN(v) ? Math.max(v, clampMin) : clampMin));
      }
      if (typeof defaultValue === 'number' && !isNaN(defaultValue)) {
        return [Math.max(defaultValue, clampMin)];
      }
      return undefined;
    }, [defaultValue, clampMin]);

    const handleValueChange = React.useCallback(
      (vals: number[]) => {
        const clamped = vals.map(v => Math.max(v, clampMin));
        onValueChange?.(clamped);
      },
      [clampMin, onValueChange],
    );

    const handleValueCommit = React.useCallback(
      (vals: number[]) => {
        setDragging(false);
        if (!isHovering && !isFocused) {
          setIsHoveringShield(false);
        }
        props.onValueCommit?.(vals);
      },
      [isHovering, isFocused, props.onValueCommit],
    );

    const handlePointerDown = React.useCallback(() => {
      setDragging(true);
    }, []);

    const range = domainMax - domainMin;
    const currentVal = normalizedValue[0] ?? clampMin;
    const fillPct = range > 0 ? ((currentVal - domainMin) / range) * 100 : 0;
    const shieldPct = clampMin > domainMin && range > 0 ? ((clampMin - domainMin) / range) * 100 : 0;

    const hasTooltip = !!tooltipContent || !!tooltipContentOnShield;

    const checkPointerInShield = React.useCallback(
      (clientX: number) => {
        if (!sliderRef.current || shieldPct === 0) return false;
        const rect = sliderRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const shieldWidth = (shieldPct / 100) * rect.width;
        return x >= 0 && x <= shieldWidth;
      },
      [shieldPct],
    );

    const handlePointerMove = React.useCallback(
      (e: React.PointerEvent) => {
        if (!hasTooltip || shieldPct === 0) return;
        if (dragging || isHovering) {
          const inShield = checkPointerInShield(e.clientX);
          setIsHoveringShield(prev => (prev !== inShield ? inShield : prev));
        }
      },
      [hasTooltip, shieldPct, dragging, isHovering, checkPointerInShield],
    );

    const handleLostPointerCapture = React.useCallback(() => {
      setIsFocused(false);
      setDragging(false);
      if (!isHovering) {
        setIsHoveringShield(false);
      }
    }, [isHovering]);

    // Thumb event handlers
    const handleThumbMouseEnter = React.useCallback(
      (e: React.MouseEvent) => {
        setIsHovering(true);
        if (shieldPct > 0) {
          setIsHoveringShield(checkPointerInShield(e.clientX));
        }
      },
      [shieldPct, checkPointerInShield],
    );

    const handleThumbMouseLeave = React.useCallback(() => {
      setIsHovering(false);
      setIsHoveringShield(false);
    }, []);

    const handleThumbFocus = React.useCallback(() => {
      setIsFocused(true);
      if (shieldPct > 0 && currentVal <= clampMin) {
        setIsHoveringShield(true);
      }
    }, [shieldPct, currentVal, clampMin]);

    const handleThumbBlur = React.useCallback(() => {
      setIsFocused(false);
      setIsHoveringShield(false);
    }, []);

    const isRainbow = variant === 'rainbow';

    const trackContent = isRainbow ? (
      <SliderPrimitive.Track
        className={cn('relative h-1.5 w-full grow overflow-hidden rounded-full', shieldPct > 0 && 'bg-layer-1')}
        style={{
          backgroundImage: `${DIM_RAINBOW}, ${FULL_RAINBOW}`,
          backgroundSize: `100% 100%, ${fillPct}% 100%`,
          backgroundRepeat: 'no-repeat, no-repeat',
        }}
      >
        <SliderPrimitive.Range className="absolute h-full bg-transparent" />
      </SliderPrimitive.Track>
    ) : (
      <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-field">
        <SliderPrimitive.Range className={cn('absolute h-full', SOLID_FULL[color]!)} />
      </SliderPrimitive.Track>
    );

    const thumbClassName = cn(
      'h-4 w-4 block rounded-full transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50',
      !isRainbow && ['bg-white shadow', 'focus-visible:ring-1 focus-visible:ring-gray-50 focus-visible:ring-opacity-25'],
      isRainbow && [
        'bg-white',
        'border-[1px] border-[rgba(237,177,11,0.5)]',
        'ring-2 ring-[rgba(237,177,11,0.5)]',
        'drop-shadow-[0_4px_6px_rgba(0,0,0,0.09)]',
      ],
      thumbClassNameProp,
      dragging && draggingThumbClassName,
    );

    // Tooltip visibility and content
    const shouldShowTooltip = hasTooltip && (dragging || isHovering || isFocused);
    const resolvedTooltipContent =
      isHovering && !dragging && !isFocused
        ? tooltipContent
        : shouldShowTooltip && isHoveringShield && tooltipContentOnShield
          ? tooltipContentOnShield
          : tooltipContent;

    const thumbElement = (
      <SliderPrimitive.Thumb
        className={thumbClassName}
        onMouseEnter={hasTooltip ? handleThumbMouseEnter : undefined}
        onMouseLeave={hasTooltip ? handleThumbMouseLeave : undefined}
        onFocus={hasTooltip ? handleThumbFocus : undefined}
        onBlur={hasTooltip ? handleThumbBlur : undefined}
      />
    );

    return (
      <SliderPrimitive.Root
        ref={node => {
          sliderRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={cn(
          'relative flex w-full touch-none select-none items-center',
          shieldPct > 0 && '[&>span:has([role=slider])]:z-[50]',
          className,
        )}
        value={normalizedValue}
        defaultValue={normalizedDefaultValue}
        min={domainMin}
        max={domainMax}
        onValueChange={handleValueChange}
        onValueCommit={handleValueCommit}
        onPointerDown={handlePointerDown}
        onPointerMove={hasTooltip ? handlePointerMove : undefined}
        onLostPointerCapture={hasTooltip ? handleLostPointerCapture : undefined}
        {...props}
      >
        {trackContent}

        {/* Shield: block clicks/drag below clampMin (double-layer for visual density) */}
        {shieldPct > 0 && (
          <>
            <div
              className="absolute top-0 bottom-0 left-0 bg-layer-1 opacity-60 rounded-l-full pointer-events-none"
              style={{ width: `${shieldPct}%` }}
            />
            <div
              className="absolute top-0 bottom-0 left-0 bg-layer-1 opacity-60 rounded-l-full pointer-events-none"
              style={{ width: `${shieldPct}%` }}
            />
          </>
        )}

        {hasTooltip ? (
          <Tooltip open={shouldShowTooltip}>
            <TooltipTrigger asChild>{thumbElement}</TooltipTrigger>
            <TooltipContent side="top" align="center" className="z-[100]">
              {resolvedTooltipContent}
            </TooltipContent>
          </Tooltip>
        ) : (
          thumbElement
        )}
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
